import React from 'react';
import { IoPrintOutline } from 'react-icons/io5';
import './NeuroAssessmentPreview.css';
import { Modal } from "react-bootstrap";

const AssessmentPreview = ({
    formData,
    formType,
    onClose,
    handleSubmit = null,
    isSubmitting = false,
    showSubmitButton = false
}) => {
    const handlePrint = () => {
        const printContent = document.getElementById('printable-content').innerHTML;
        const printWindow = window.open('', '_blank', 'width=800,height=600');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Assessment Form</title>
                    <style>
                        body { 
                            font-family: Arial, sans-serif; 
                            margin: 20px; 
                            line-height: 1.4;
                        }
                        .preview-title h3 { 
                            text-align: center; 
                            margin-bottom: 30px; 
                            font-size: 18px;
                        }
                        .preview-section { 
                            margin-bottom: 25px; 
                            page-break-inside: avoid; 
                        }
                        .preview-section-title { 
                            border-bottom: 2px solid #333; 
                            padding-bottom: 5px; 
                            margin-bottom: 15px; 
                            font-size: 16px;
                        }
                       .info-grid {
                            display: grid;
                            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                            gap: 5px;
                        }

                        .info-row {
                            display: flex;
                            flex-direction: column;
                        }

                        .info-grid .label,
                        .sub-title {
                            font-size: 14px;
                            font-weight: 600;
                            color: #000;
                        }

                        .assessment-table table { 
                            width: 100%; 
                            border-collapse: collapse; 
                            margin-bottom: 15px; 
                        }
                        .assessment-table th, .assessment-table td { 
                            border: 1px solid #333; 
                            padding: 8px; 
                            text-align: center; 
                            font-size: 12px;
                        }
                        .muscle-name-cell { 
                            text-align: left !important; 
                        }
                        .text-section { 
                            margin-bottom: 15px; 
                        }
                        .text-section strong { 
                            display: block; 
                            margin-bottom: 5px; 
                        }
                        .sub-title, .sub-head { 
                            font-weight: bold; 
                            margin: 15px 0 10px 0; 
                            display: block; 
                        }
                        .assessment-legends {
                            margin-top: 20px;
                            font-size: 12px;
                            page-break-inside: avoid;
                        }
                        .legend-section {
                            margin-bottom: 15px;
                            page-break-inside: avoid;
                        }
                        .legend-section h4 {
                            margin: 0 0 5px 0;
                            font-size: 12px;
                            font-weight: bold;
                        }
                        .legend-items {
                            display: flex;
                            flex-wrap: wrap;
                            gap: 5px;
                        }
                        .legend-item {
                            white-space: nowrap;
                        }
                        .additional-info {
                            margin-top: 10px;
                        }
                        @media print {
                            body { margin: 0; }
                            .preview-section { page-break-inside: avoid; }
                        }
                    </style>
                </head>
                <body>
                    ${printContent}
                    <script>
                        window.onload = function() {
                            window.print();
                            window.onafterprint = function() {
                                window.close();
                            }
                        }
                    </script>
                </body>
            </html>
        `);

        printWindow.document.close();
    };

    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return '--';
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (typeof value === 'string') {
            return value.charAt(0).toUpperCase() + value.slice(1);
        }
        return value;
    };

    const getMuscleDisplayName = (muscleKey) => {
        const muscleMap = {
            'shoulderFlexors': 'Shoulder Flexors',
            'shoulderExtensors': 'Shoulder Extensors',
            'shoulderIr': 'Shoulder IR',
            'shoulderEr': 'Shoulder ER',
            'shoulderAbductors': 'Shoulder Abductors',
            'shoulderAdductors': 'Shoulder Adductors',
            'elbowFlexors': 'Elbow Flexors',
            'elbowExtensors': 'Elbow Extensors',
            'wristFlexors': 'Wrist Flexors',
            'wristExtensors': 'Wrist Extensors',
            'hipFlexors': 'Hip Flexors',
            'hipExtensors': 'Hip Extensors',
            'hipAbductors': 'Hip Abductors',
            'hipAdductors': 'Hip Adductors',
            'kneeFlexors': 'Knee Flexors',
            'kneeExtensor': 'Knee Extensor',
            'dorsiFlexors': 'Dorsi Flexors',
            'plantarFlexors': 'Plantar Flexors'
        };
        return muscleMap[muscleKey] || muscleKey;
    };

    const getFormConfig = () => {
        switch (formType) {
            case 'reassess':
                return {
                    title: 'PHYSIOTHERAPY RE-ASSESSMENT FORM',
                    sections: [
                        {
                            title: 'Patient Information',
                            type: 'info_grid',
                            fields: [
                                { label: 'Patient ID', key: 'id' },
                                { label: 'Name', key: 'name' },
                                { label: 'Age', key: 'age' },
                                { label: 'Gender', key: 'gender' },
                                { label: 'Hand Dominance', key: 'hand_dominance' }
                            ]
                        },
                        {
                            title: 'Assessment Details',
                            type: 'info_grid',
                            fields: [
                                { label: 'Balance', key: 'balance' },
                                { label: 'Gait', key: 'gait' },
                                { label: 'Therapy Type', key: 'therapy_type' }
                            ]
                        },
                        {
                            title: 'Motor Examination',
                            type: 'motor_table_reassess'
                        }
                    ]
                };
            case 'neuro':
                return {
                    title: 'NEURO REHABILITATION PHYSIOTHERAPY ASSESSMENT FORM',
                    sections: [
                        {
                            title: 'Patient Information',
                            type: 'info_grid',
                            fields: [
                                { label: 'Patient ID', key: 'id' },
                                { label: 'Name', key: 'name' },
                                { label: 'Age', key: 'age' },
                                { label: 'Gender', key: 'gender' },
                                { label: 'Contact Number', key: 'contact_number' },
                                { label: 'Hand Dominance', key: 'hand_dominance' }
                            ]
                        },
                        {
                            title: 'Chief Complaints',
                            type: 'info_grid',
                            fields: [
                                { label: 'Weakness of UL/LL', key: 'chiefComplaint1' },
                                { label: 'Difficulty in walking', key: 'chiefComplaint2' },
                                { label: 'Difficulty in performing ADLs', key: 'chiefComplaint3' },
                                { label: 'Pain present', key: 'chiefComplaint4' },
                                { label: 'Any other', key: 'chiefComplaint5' }
                            ]
                        },
                        {
                            title: 'Clinical History',
                            type: 'info_grid',
                            fields: [
                                { label: 'Present History', key: 'history.present' },
                                { label: 'Past History', key: 'history.past' },
                                { label: 'Medical History', key: 'history.medical' },
                                { label: 'Surgical History', key: 'history.surgical' }
                            ]
                        },
                        {
                            title: 'General Examination',
                            type: 'info_grid',
                            fields: [
                                { label: 'Pulse Rate', key: 'general_examination.pulse', unit: 'bpm' },
                                { label: 'Blood Pressure', key: 'general_examination.bp', unit: 'mmHg' },
                                { label: 'Built', key: 'built' }
                            ]
                        },
                        {
                            title: 'CNS Examination',
                            type: 'mixed',
                            fields: [
                                { label: 'Conscious', key: 'conscious', type: 'boolean' },
                                { label: 'Alert', key: 'alert', type: 'boolean' },
                                { label: 'Obeys', key: 'obeys', type: 'boolean' },
                                { label: 'Oriented', key: 'oriented', type: 'boolean' },
                                { label: 'Speech', key: 'speech' },
                                { label: 'Cranial Nerve Examination', key: 'cne_examination', type: 'textarea' }
                            ]
                        },
                        {
                            title: 'Motor Examination',
                            type: 'motor_table',
                            muscles: [
                                'Shoulder Flexion', 'Shoulder Extension', 'Shoulder Abduction', 'Shoulder Adduction',
                                'Elbow Flexion', 'Elbow Extension', 'Wrist Flexion', 'Wrist Extension',
                                'Hip Flexion', 'Hip Extension', 'Hip Abduction', 'Hip Adduction',
                                'Knee Flexion', 'Knee Extension', 'Ankle Dorsiflexion', 'Ankle Plantarflexion'
                            ]
                        },
                        {
                            title: 'Deep Tendon Reflexes',
                            type: 'reflex_table',
                            reflexes: ['Biceps Jerk', 'Supinator Jerk', 'Triceps Jerk', 'Knee Jerk', 'Ankle Jerk']
                        },
                        {
                            title: 'Sensory Examination',
                            type: 'sensory_tables'
                        },
                        {
                            title: 'Coordination',
                            type: 'info_grid',
                            fields: [
                                { label: 'Finger to nose', key: 'coordination.finger_to_nose' },
                                { label: 'Finger opposition', key: 'coordination.finger_opposition' },
                                { label: 'Mass grasp', key: 'coordination.mass_grasp' },
                                { label: 'Pronation/Supination', key: 'coordination.pronation_supination' },
                                { label: 'Rebound test', key: 'coordination.rebound_test' },
                                { label: 'Tapping (Hand)', key: 'coordination.tapping_hand' },
                                { label: 'Tapping (Foot)', key: 'coordination.tapping_foot' },
                                { label: 'Heel to knee', key: 'coordination.heel_to_knee' },
                                { label: 'Drawing circle (Hand)', key: 'coordination.drawing_circle_hand' },
                                { label: 'Drawing circle (Foot)', key: 'coordination.drawing_circle_foot' }
                            ]
                        },
                        {
                            title: 'Balance',
                            type: 'info_grid',
                            fields: [
                                { label: 'Sitting', key: 'balance.sitting' },
                                { label: 'Standing', key: 'balance.standing' }
                            ]
                        },
                        {
                            title: 'Additional Assessments',
                            type: 'text_sections',
                            fields: [
                                { label: 'Posture', key: 'posture' },
                                { label: 'Gait', key: 'gait' },
                                { label: 'Functional Diagnosis', key: 'functional_diagnosis' },
                                { label: 'Treatment Goal', key: 'treatment_goal' }
                            ]
                        },
                        {
                            title: 'Assessment Scores',
                            type: 'scores',
                            sections: [
                                {
                                    title: 'Brunnstrom Stages',
                                    fields: [
                                        { label: 'Admission', key: 'brunnstorm.admission', prefix: 'Stage ' },
                                        { label: 'Discharge', key: 'brunnstorm.discharge', prefix: 'Stage ' }
                                    ]
                                },
                                {
                                    title: 'Pain Rating',
                                    fields: [
                                        { label: 'Admission', key: 'pain_rating.admission', suffix: '/10' },
                                        { label: 'Discharge', key: 'pain_rating.discharge', suffix: '/10' }
                                    ]
                                }
                            ]
                        }
                    ]
                };
            case 'general':
                return {
                    title: 'GENERAL PHYSIOTHERAPY ASSESSMENT FORM',
                    sections: [
                        {
                            title: 'Patient Information',
                            type: 'info_grid',
                            fields: [
                                { label: 'Patient ID', key: 'id' },
                                { label: 'Name', key: 'name' },
                                { label: 'Age', key: 'age' },
                                { label: 'Gender', key: 'gender' },
                                { label: 'Contact Number', key: 'contact_number' }
                            ]
                        },
                        {
                            title: 'Clinical History',
                            type: 'text_sections',
                            fields: [
                                { label: 'Chief Complaints', key: 'chief_complaints' },
                                { label: 'Present History', key: 'present_history' },
                                { label: 'Past History', key: 'past_history' },
                                { label: 'Medical History', key: 'medical_history' },
                                { label: 'Personal History', key: 'personal_history' }
                            ]
                        },
                        {
                            title: 'Pain Assessment',
                            type: 'info_grid',
                            fields: [
                                { label: 'Site', key: 'site' },
                                { label: 'Side', key: 'side' },
                                { label: 'Onset', key: 'onset' },
                                { label: 'Duration', key: 'duration' },
                                { label: 'Type', key: 'type' },
                                { label: 'Nature', key: 'nature' },
                                { label: 'Behaviour', key: 'behaviour' },
                                { label: 'Aggravating Factors', key: 'aggravating_factor' },
                                { label: 'Relieving Factors', key: 'relieving_factor' },

                            ]
                        },
                        {
                            title: 'General Examination',
                            type: 'info_grid',
                            fields: [
                                { label: 'Body Type', key: 'built' },
                                { label: 'Sleeping Pattern', key: 'sleep' },
                                { label: 'On Observation', key: 'on_observation' },
                                { label: 'Posture Assessment', key: 'posture' },
                                { label: 'External Aids', key: 'external_aids' },
                                { label: 'On Examination', key: 'on_examination' }
                            ]
                        },
                        {
                            title: 'Palpitation',
                            type: 'info_grid',
                            fields: [
                                { label: 'Temperature', key: 'temperature' },
                                { label: 'Swelling', key: 'swelling' },
                                { label: 'Tenderness', key: 'tenderness' },
                                { label: 'Scar', key: 'scar' }
                            ]
                        },
                        {
                            title: 'Final Assessment',
                            type: 'text_sections',
                            fields: [
                                { label: 'Sensory Examination', key: 'sensory' },
                                { label: 'ROM', key: 'rom' },
                                { label: 'Specific Tests', key: 'specific_test' },
                                { label: 'Final Diagnosis', key: 'functional_diagnosis' },
                                { label: 'Treatment Goal', key: 'treatment_goal' }
                            ]
                        }
                    ]
                };
            case 'ortho':
                return {
                    title: 'ORTHO ASSESSMENT FORM',
                    sections: [
                        {
                            title: 'Patient Information',
                            type: 'info_grid',
                            fields: [
                                { label: 'Patient ID', key: 'id' },
                                { label: 'Name', key: 'name' },
                                { label: 'Age', key: 'age' },
                                { label: 'Gender', key: 'gender' },
                                { label: 'Contact Number', key: 'contact_number' }
                            ]
                        },
                        {
                            title: 'Clinical History',
                            type: 'text_sections',
                            fields: [
                                { label: 'Chief Complaints', key: 'chief_complaints' },
                                { label: 'History of Present Illness', key: 'history_of_present_illness' },
                                { label: 'Past Medical History', key: 'past_medical_history' },
                                { label: 'Surgical History', key: 'surgical_history' },
                                { label: 'Personal History', key: 'personal_history' },
                                { label: 'Previous Treatment', key: 'previous_treatment' }
                            ]
                        },
                        {
                            title: 'Pain Assessment',
                            type: 'pain_assessment_ortho',
                            fields: [
                                { label: 'NPRS', key: 'nprs' },
                                { label: 'Site', key: 'site' },
                                { label: 'Nature', key: 'nature' },
                                { label: 'Onset', key: 'onset' },
                                { label: 'Aggravating Factors', key: 'aggravating_factor' },
                                { label: 'Relieving Factors', key: 'relieving_factor' },
                                { label: 'Diurnal Variation', key: 'diurnal_variation' }
                            ]
                        },
                        {
                            title: 'Sleep Assessment',
                            type: 'info_grid',
                            fields: [
                                { label: 'Disturbed Sleep', key: 'sleep' }
                            ]
                        },
                        {
                            title: 'Objective Assessment - Observation',
                            type: 'info_grid',
                            fields: [
                                { label: 'Body Type', key: 'built' },
                                { label: 'Posture', key: 'posture' },
                                { label: 'External Aids', key: 'external_aids' }
                            ]
                        },
                        {
                            title: 'Objective Assessment - Palpation',
                            type: 'info_grid',
                            fields: [
                                { label: 'Temperature', key: 'temperature' },
                                { label: 'Swelling/Oedema', key: 'swelling' },
                                { label: 'Tenderness', key: 'tenderness' },
                                { label: 'Trigger Points', key: 'trigger_points' },
                                { label: 'Scar', key: 'scar' }
                            ]
                        },
                        {
                            title: 'Physical Examination - Sensory',
                            type: 'sensory_ortho',
                            fields: [
                                { label: 'Superficial', key: 'sensory.superficial' },
                                { label: 'Deep', key: 'sensory.deep' }
                            ]
                        },
                        {
                            title: 'Physical Examination - Reflex',
                            type: 'info_grid',
                            fields: [
                                { label: 'Reflex Assessment', key: 'reflex' }
                            ]
                        },
                        {
                            title: 'Physical Examination - Motor',
                            type: 'motor_ortho',
                            fields: [
                                { label: 'Muscle Tone', key: 'motor.muscle_tone' },
                                { label: 'AROM', key: 'motor.arom' },
                                { label: 'PROM', key: 'motor.prom' },
                                { label: 'Capsular Pattern', key: 'motor.capsular_pattern' },
                                { label: 'Resisted Isometric Test', key: 'motor.resisted_isometric' },
                                { label: 'MMT', key: 'motor.mmt' },
                                { label: 'Girth Measure', key: 'motor.girth_measure' },
                                { label: 'Limb Length Discrepancy', key: 'motor.limb_length' },
                                { label: 'Proximal & Distal Joint Screening', key: 'motor.proximal' }
                            ]
                        },
                        {
                            title: 'Additional Assessments',
                            type: 'text_sections',
                            fields: [
                                { label: 'Gait Assessment', key: 'gait' },
                                { label: 'Functional Evaluation', key: 'functional_evaluation' },
                                { label: 'Interpretation/Comments', key: 'interpretation' },
                                { label: 'Special Tests', key: 'special_tests' },
                                { label: 'Investigations', key: 'investigations' },
                                { label: 'Physical Diagnosis', key: 'physical_diagnosis' },
                                { label: 'Provisional Diagnosis', key: 'provisional_diagnosis' },
                                { label: 'Special Notes', key: 'special_notes' },
                                { label: 'Problem List (ICF Model)', key: 'problem_list' }
                            ]
                        },
                        {
                            title: 'Goals and Care Plan',
                            type: 'goals_care_plan',
                            fields: [
                                { label: 'Short Term Goals', key: 'goals.shor_term' },
                                { label: 'Long Term Goals', key: 'goals.long_term' },
                                { label: 'Care Plan', key: 'care_plan' },
                                { label: 'Home Program', key: 'home_program' }
                            ]
                        }
                    ]
                };
            default:
                return { title: 'Assessment Form', sections: [] };
        }
    };

    const getValue = (key) => {
        const keys = key.split('.');
        let value = formData;
        for (const k of keys) {
            value = value?.[k];
        }

        if (key === 'sleep' && typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        if (key === 'built' && value) {
            const builtMapping = {
                'obese': 'Obese (Endomorphic)',
                'moderate': 'Moderate (Mesomorphic)',
                'thin': 'Thin (Ectomorphic)'
            };
            return builtMapping[value] || formatValue(value);
        }

        return formatValue(value);
    };

    const getMotorFunctionValue = (muscleName, side, field) => {
        const entry = formData.motor_function?.find(
            mf => mf.muscle === muscleName && mf.side === side
        );
        return formatValue(entry?.[field]);
    };

    // New function specifically for reassessment motor function
    const getReassessmentMotorValue = (muscleName, side, field) => {
        const entry = formData.motor_function?.find(
            mf => mf.muscle === muscleName && mf.side === side.toLowerCase()
        );
        return formatValue(entry?.[field]);
    };

    const getReflexValue = (reflexType, side) => {
        const reflex = formData.reflexes?.find(
            r => r.reflex_type === reflexType && r.side === side
        );
        return formatValue(reflex?.response);
    };

    const getSensoryValue = (category, sensation, side) => {
        return formatValue(formData.sensory?.[category]?.[sensation]?.[side]);
    };

    const renderSection = (section) => {
        switch (section.type) {
            case 'info_grid':
                return (
                    <div className="info-grid">
                        {section.fields.map((field, index) => (
                            <div key={index} className="info-row">
                                <span className="label">{field.label}:</span>
                                <span className="value">
                                    {getValue(field.key)}{field.unit ? ` ${field.unit}` : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                );

            case 'complaints':
                const complaints = section.fields
                    .map(field => getValue(field.key))
                    .filter(complaint => complaint !== '--');
                return (
                    <div className="complaints-list">
                        {complaints.length > 0 ? complaints.map((complaint, index) => (
                            <div key={index} className="complaint-item">
                                <span className="complaint-number">{index + 1}.</span>
                                <span className="complaint-text">{complaint}</span>
                            </div>
                        )) : (
                            <div className="complaint-item">
                                <span className="complaint-text">No complaints specified</span>
                            </div>
                        )}
                    </div>
                );

            case 'text_sections':
                return (
                    <div className="text-sections">
                        {section.fields.map((field, index) => (
                            <div key={index} className="text-section">
                                <strong>{field.label}:</strong>
                                <p>{getValue(field.key)}</p>
                            </div>
                        ))}
                    </div>
                );

            case 'mixed':
                return (
                    <div className="mixed-section">
                        <div className="info-grid">
                            {section.fields.map((field, index) => (
                                <div key={index} className="info-row">
                                    <span className="label">{field.label}:</span>
                                    <span className="value">{getValue(field.key)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'motor_table_reassess':
                const uniqueMuscles = [...new Set(formData.motor_function?.map(mf => mf.muscle) || [])];

                return (
                    <div>
                        <div className="assessment-table">
                            <table className="motor-examination-table">
                                <thead>
                                    <tr>
                                        <th className='muscle-header' rowSpan="2">Muscle</th>
                                        <th className='assessment-header' colSpan="2">MMT</th>
                                        <th className='assessment-header' colSpan="2">ROM</th>
                                        <th className='assessment-header' colSpan="2">Tone</th>
                                    </tr>
                                    <tr>
                                        <th className='assessment-header'>RT</th>
                                        <th className='assessment-header'>LT</th>
                                        <th className='assessment-header'>RT</th>
                                        <th className='assessment-header'>LT</th>
                                        <th className='assessment-header'>RT</th>
                                        <th className='assessment-header'>LT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {uniqueMuscles.map((muscle, index) => (
                                        <tr key={index}>
                                            <td className="muscle-name-cell">{getMuscleDisplayName(muscle)}</td>
                                            <td>{getReassessmentMotorValue(muscle, 'RT', 'mmt')}</td>
                                            <td>{getReassessmentMotorValue(muscle, 'LT', 'mmt')}</td>
                                            <td>{getReassessmentMotorValue(muscle, 'RT', 'rom')}</td>
                                            <td>{getReassessmentMotorValue(muscle, 'LT', 'rom')}</td>
                                            <td>{getReassessmentMotorValue(muscle, 'RT', 'tone')}</td>
                                            <td>{getReassessmentMotorValue(muscle, 'LT', 'tone')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="assessment-legends" style={{ marginTop: '20px', fontSize: '12px' }}>
                            <div className="legend-section">
                                <h4>MMT Scale:</h4>
                                <div className="legend-items">
                                    <span className="legend-item">0 - No contraction</span>
                                    <span className="legend-item">1 - Trace</span>
                                    <span className="legend-item">2 - Poor</span>
                                    <span className="legend-item">3 - Fair</span>
                                    <span className="legend-item">4 - Good</span>
                                    <span className="legend-item">5 - Normal</span>
                                </div>
                            </div>
                            <div className="legend-section">
                                <h4>ROM Scale:</h4>
                                <div className="legend-items">
                                    <span className="legend-item">WNL - Within Normal Limits</span>
                                    <span className="legend-item">Limited - Reduced range</span>
                                    <span className="legend-item">Restricted - Significantly limited</span>
                                    <span className="legend-item">Contracture - Fixed limitation</span>
                                </div>
                            </div>
                            <div className="legend-section">
                                <h4>Tone Scale:</h4>
                                <div className="legend-items">
                                    <span className="legend-item">0 - Flaccid</span>
                                    <span className="legend-item">1 - Hypotonic</span>
                                    <span className="legend-item">2 - Normal</span>
                                    <span className="legend-item">3 - Hypertonic</span>
                                    <span className="legend-item">4 - Spastic</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'motor_table':
                return (
                    <div className="assessment-table">
                        <table className="motor-examination-table">
                            <thead>
                                <tr>
                                    <th className='muscle-header' rowSpan="2">Muscle</th>
                                    <th className='assessment-header' colSpan="2">MMT</th>
                                    <th className='assessment-header' colSpan="2">ROM</th>
                                    <th className='assessment-header' colSpan="2">Tone</th>
                                </tr>
                                <tr>
                                    <th className='assessment-header'>RT</th>
                                    <th className='assessment-header'>LT</th>
                                    <th className='assessment-header'>RT</th>
                                    <th className='assessment-header'>LT</th>
                                    <th className='assessment-header'>RT</th>
                                    <th className='assessment-header'>LT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {section.muscles.map((muscle, index) => (
                                    <tr key={index}>
                                        <td className="muscle-name-cell">{muscle}</td>
                                        <td>{getMotorFunctionValue(muscle, 'RT', 'mmt')}</td>
                                        <td>{getMotorFunctionValue(muscle, 'LT', 'mmt')}</td>
                                        <td>{getMotorFunctionValue(muscle, 'RT', 'rom')}</td>
                                        <td>{getMotorFunctionValue(muscle, 'LT', 'rom')}</td>
                                        <td>{getMotorFunctionValue(muscle, 'RT', 'tone')}</td>
                                        <td>{getMotorFunctionValue(muscle, 'LT', 'tone')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );

            case 'reflex_table':
                return (
                    <div>
                        <div className="assessment-table">
                            <table className="motor-examination-table">
                                <thead>
                                    <tr>
                                        <th className='muscle-header'>Reflex</th>
                                        <th className='assessment-header'>Right</th>
                                        <th className='assessment-header'>Left</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {section.reflexes.map((reflexType, index) => (
                                        <tr key={index}>
                                            <td className="muscle-name-cell">{reflexType}</td>
                                            <td>{getReflexValue(reflexType, 'Right')}</td>
                                            <td>{getReflexValue(reflexType, 'Left')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="additional-info">
                            <strong>Babinski Sign:</strong> {formatValue(formData.babinski)}
                        </div>
                        <div className="additional-info">
                            <strong>Babinski Details:</strong> {formatValue(formData.babinski_details)}
                        </div>
                    </div>
                );

            case 'sensory_tables':
                return (
                    <div>
                        <label className="sub-title">Superficial Sensations:</label>
                        <div className="assessment-table">
                            <table className="motor-examination-table">
                                <thead>
                                    <tr>
                                        <th className='muscle-header'>Sensation</th>
                                        <th className='assessment-header'>RT</th>
                                        <th className='assessment-header'>LT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(formData.sensory?.superficial || {}).map(sensation => (
                                        <tr key={sensation}>
                                            <td className="muscle-name-cell">{sensation.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
                                            <td>{getSensoryValue('superficial', sensation, 'RT')}</td>
                                            <td>{getSensoryValue('superficial', sensation, 'LT')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <label className="sub-title">Deep Sensations:</label>
                        <div className="assessment-table">
                            <table className="motor-examination-table">
                                <thead>
                                    <tr>
                                        <th className='muscle-header' >Sensation</th>
                                        <th className='assessment-header'>RT</th>
                                        <th className='assessment-header'>LT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(formData.sensory?.deep || {}).map(sensation => (
                                        <tr key={sensation}>
                                            <td className="muscle-name-cell">{sensation.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
                                            <td>{getSensoryValue('deep', sensation, 'RT')}</td>
                                            <td>{getSensoryValue('deep', sensation, 'LT')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <label className="sub-title">Cortical Sensations:</label>
                        <div className="assessment-table">
                            <table className="motor-examination-table">
                                <thead>
                                    <tr>
                                        <th className='muscle-header'>Sensation</th>
                                        <th className='assessment-header'>RT</th>
                                        <th className='assessment-header'>LT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(formData.sensory?.cortical || {}).map(sensation => (
                                        <tr key={sensation}>
                                            <td className="muscle-name-cell">{sensation.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
                                            <td>{getSensoryValue('cortical', sensation, 'RT')}</td>
                                            <td>{getSensoryValue('cortical', sensation, 'LT')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );

            case 'scores':
                return (
                    section.sections.map((scoreSection, index) => (
                        <>
                            <label className='sub-head'>{scoreSection.title}</label>
                            <div className="info-grid">
                                {scoreSection.fields.map((field, fieldIndex) => (
                                    <div key={fieldIndex} className="info-row">
                                        <span className="label">{field.label}:</span>
                                        <span className="value">
                                            {field.prefix || ''}{getValue(field.key)}{field.suffix || ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    ))
                );
            case 'pain_assessment_ortho':
                return (
                    <div className="pain-assessment-ortho-section">
                        <div className="info-grid">
                            {section.fields.slice(0, 4).map((field, index) => (
                                <div key={index} className="info-row">
                                    <span className="label">{field.label}:</span>
                                    <span className="value">{getValue(field.key)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="text-sections">
                            {section.fields.slice(4).map((field, index) => (
                                <div key={index} className="text-section">
                                    <strong>{field.label}:</strong>
                                    <p>{getValue(field.key)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'sensory_ortho':
                return (
                    <div className="sensory-ortho-section">
                        <div className="info-grid">
                            {section.fields.map((field, index) => (
                                <div key={index} className="info-row">
                                    <span className="label">{field.label}:</span>
                                    <span className="value">{getValue(field.key)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'motor_ortho':
                return (
                    <div className="motor-ortho-section">
                        <div className="info-grid">
                            {section.fields.map((field, index) => (
                                <div key={index} className="info-row">
                                    <span className="label">{field.label}:</span>
                                    <span className="value">{getValue(field.key)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'goals_care_plan':
                return (
                    <div className="goals-care-plan-section">
                        <div className="text-sections">
                            {section.fields.map((field, index) => (
                                <div key={index} className="text-section">
                                    <strong>{field.label}:</strong>
                                    <p>{getValue(field.key)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const config = getFormConfig();

    return (
        <Modal show={true} centered size="lg" onHide={onClose}>
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body style={{ textAlign: "left", display: 'flex', flexDirection: 'column' }} >

                {/* This div contains only the printable content */}
                <div id="printable-content" className="preview-content">
                    <div className="preview-title">
                        <h3>{config.title}</h3>
                    </div>

                    {config.sections.map((section, index) => (
                        <div key={index} className="preview-section">
                            <h3 className="preview-section-title">{section.title}</h3>
                            {renderSection(section)}
                        </div>
                    ))}
                </div>

                <div className="preview-controls">
                    <button onClick={onClose} className="cancel-btn preview-btn">
                        Close
                    </button>
                    <button onClick={handlePrint} className="preview-btn print-bttn" title="Print Preview">
                        <IoPrintOutline /> Print
                    </button>
                    {showSubmitButton && (
                        <button
                            onClick={handleSubmit}
                            className="preview-btn submit-btn"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AssessmentPreview;